/**
 * PDFViewerPanel - Container component for PDF viewing with controls
 * Combines PDFRenderer, HighlightOverlay, and navigation controls
 * Requirements: 1.1, 1.2, 1.5, 3.1
 */

import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import ButtonGroup from '@mui/material/ButtonGroup';

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import PDFRenderer from './PDFRenderer';
import HighlightOverlay from './HighlightOverlay';
import { useHighlightingContext } from '../../contexts';
import { EmptyState } from '../shared/EmptyState';

interface PDFViewerPanelProps {
  documentUrl: string;
  documentId: string;
  totalPages: number;
}

const PDFViewerPanel: React.FC<PDFViewerPanelProps> = ({
  documentUrl,
  totalPages: initialTotalPages
}) => {
  const highlightingContext = useHighlightingContext();
  const { state, actions } = highlightingContext;
  const [actualTotalPages, setActualTotalPages] = useState(initialTotalPages);
  
  // Use context state instead of local state
  const currentPage = state.currentPage;



  // Navigation controls using context
  const handleFirstPage = useCallback(() => {
    actions.setCurrentPage(1);
  }, [actions]);

  const handlePreviousPage = useCallback(() => {
    actions.setCurrentPage(Math.max(currentPage - 1, 1));
  }, [currentPage, actions]);

  const handleNextPage = useCallback(() => {
    actions.setCurrentPage(Math.min(currentPage + 1, actualTotalPages));
  }, [currentPage, actualTotalPages, actions]);

  const handleLastPage = useCallback(() => {
    actions.setCurrentPage(actualTotalPages);
  }, [actualTotalPages, actions]);

  // No document file to render (e.g. the content proxy failed) — say so plainly instead of
  // handing react-pdf an empty URL. Field review still works without the preview (UX4 step 7).
  if (!documentUrl) {
    return (
      <Paper
        elevation={1}
        sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <EmptyState
          icon={<PictureAsPdfIcon sx={{ fontSize: 48 }} />}
          message="Document preview unavailable"
          description="The original file couldn't be loaded. You can still review and validate the extracted fields."
          minHeight="60%"
        />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Controls Toolbar */}
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 48,
          borderBottom: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
          px: 1,
        }}
      >
        {/* Navigation Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="First Page">
              <span>
                <IconButton
                  onClick={handleFirstPage}
                  disabled={currentPage === 1}
                  size="small"
                >
                  <FirstPageIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Previous Page">
              <span>
                <IconButton
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  size="small"
                >
                  <NavigateBeforeIcon />
                </IconButton>
              </span>
            </Tooltip>
          </ButtonGroup>

          <Typography variant="body2" sx={{ mx: 1, minWidth: 80, textAlign: 'center' }}>
            {currentPage} of {actualTotalPages}
          </Typography>

          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="Next Page">
              <span>
                <IconButton
                  onClick={handleNextPage}
                  disabled={currentPage === actualTotalPages}
                  size="small"
                >
                  <NavigateNextIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Last Page">
              <span>
                <IconButton
                  onClick={handleLastPage}
                  disabled={currentPage === actualTotalPages}
                  size="small"
                >
                  <LastPageIcon />
                </IconButton>
              </span>
            </Tooltip>
          </ButtonGroup>
        </Box>

      </Toolbar>

      {/* PDF Viewer Container */}
      <Box
        data-testid="pdf-viewer-container"
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'auto',
          backgroundColor: 'grey.100',
          minHeight: 0, // Allow flex shrinking
        }}
      >
        {/* PDF Renderer */}
        <PDFRenderer
          documentUrl={documentUrl}
          onLoadSuccess={(numPages: number) => {
            console.log('PDF loaded with', numPages, 'pages');
            setActualTotalPages(numPages);
          }}
          onLoadError={(error: Error) => console.error('PDF load error:', error)}
        />
        
        {/* Highlight Overlay */}
        <HighlightOverlay
          pageNumber={currentPage}
          containerWidth={highlightingContext.state.pdfViewport?.width}
          containerHeight={highlightingContext.state.pdfViewport?.height}
        />
      </Box>
    </Paper>
  );
};

export default PDFViewerPanel;